/* eslint-disable import/no-deprecated */
import { useHover } from '@fullstory/react-hooks';
import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { Icon } from '../../../components/Icon';
import { identifyComponent } from '../../../util';
import { Box } from '../../Box';
import { Flex } from '../../Flex';
import { SortDirection } from '../TableSortContext';
import css from './index.module.scss';
import { TableHeadCellLabel } from './Label';

interface TableHeadCellSortLabelProps extends React.ComponentProps<typeof TableHeadCellLabel> {
  active?: boolean;
  direction?: SortDirection;

  /**
   * Determines the initial sort direction. Defaults to: `asc`
   *
   * Why? Some value types might make the most sense to the user to default to a `desc` sort for the
   * most common sort.
   */
  initialDirection?: SortDirection;
  onClick?: () => void;
}

export const TableHeadCellSortLabel: React.FunctionComponent<TableHeadCellSortLabelProps> = ({
  children,
  active = false,
  direction,
  initialDirection = 'asc',
  onClick,
  ...rest
}) => {
  const { hovered, hoverProps } = useHover();
  const derivedDirection = active ? direction : initialDirection;

  return (
    <Flex
      data-component="TableHeadCellSortLabel"
      inline
      onClick={onClick}
      justifyContent="flex-start"
      className={css.sortLabel}
      {...hoverProps}
      {...identifyComponent('table-head-cell-sort-label')}
    >
      <TableHeadCellLabel {...rest}>{children}</TableHeadCellLabel>
      {hovered || active ? (
        <Box
          minWidth={16}
          marginLeft={8}
          className={classNames(css.sortIcon, {
            [css.sortIconDesc]: derivedDirection === 'desc',
          })}
        >
          <Icon
            testId="sorting-icon"
            name="arrow-up"
            color={active ? 'default' : 'secondary'}
            size={16}
          />
        </Box>
      ) : (
        // placeholder that is the same size as sort arrow so columns don't shift when hovering
        <Box minWidth={16} marginLeft={8} />
      )}
    </Flex>
  );
};
